<template>
  <div :class="currentClass">
    <div
      v-if="passwordResetRequired && mode !== AuthFormType.PasswordReset"
      class="mt-4"
    >
      <vetster-notice color="red">
        <div>
          {{ $t('login.errors.password-reset-required-slim') }}
          <a
            href="#"
            class="underline"
            @click.prevent="mode = AuthFormType.PasswordReset"
          >
            {{ $t('login.reset-your-password').toLowerCase() }}.
          </a>
        </div>
      </vetster-notice>
    </div>

    <div
      v-if="mode === AuthFormType.OTP"
      class="max-w-sm mx-auto"
    >
      <vetster-one-time-password-form
        :channel="getChannel"
        :email="getEmail"
        :phone-number="getPhoneNumber"
        :is2-f-a="is2FA"
        :is-loading="loading"
        :error="error"
        :is-email-verification="showEmailVerification"
        @complete="authenticateWithCode"
        @updated="clearError"
        @switch="switchMode"
      />
    </div>
    <div v-else-if="mode === AuthFormType.PasswordLogin">
      <vetster-password-login
        :email="inputs.email"
        :is-loading="loading"
        :error="error"
        @complete="authenticateWithPassword"
        @forgot-password="forgotPassword"
      />
    </div>
    <vetster-reset-password
      v-else-if="mode === AuthFormType.PasswordReset"
      :email="inputs.email"
      @complete="authenticateWithPassword"
    />
    <div v-else>
      <form
        data-testid="login-form"
        @submit.prevent="attemptPasswordlessLogin"
      >
        <div class="space-y-6">
          <vetster-input
            ref="emailInput"
            v-model.trim="v$.inputs.email.$model"
            name="email"
            :label="$t('common.email')"
            autocomplete="off"
            :shake="shouldShake('email', v$.inputs.email.$error)"
            :error="hasError('email', v$.inputs.email.$error)"
            :feedback="getErrorMessage('email', v$.inputs.email.$error, $t('login.errors.please-enter-email'))"
            is-rebranded
          />
          <vetster-button
            id="login-continue-btn"
            type="button"
            :variant="buttonVariant"
            size="lg"
            :disabled="isContinueLoading"
            :is-loading="isContinueLoading"
            class="w-full"
            @click="attemptPasswordlessLogin"
          >
            {{ $t('app.continue') }}
          </vetster-button>
        </div>
      </form>
      <vetster-auth-socialite-buttons
        v-if="showSocial"
        :loading="isContinueLoading"
        @action-completed="passThroughEvent"
        @switch="setAuthModalMode"
      />
    </div>
  </div>
</template>

<script>
import useVuelidate from '@vuelidate/core';
import {
  email as emailRule,
  required,
} from '@vuelidate/validators';
import isNull from 'lodash/isNull';
import omitBy from 'lodash/omitBy';
import { mapActions, mapGetters } from 'vuex';

import VetsterAuthSocialiteButtons from '@/brand-components/VetsterAuthSocialiteButtons';
import VetsterNotice from '@/brand-components/VetsterNotice';
import VetsterOneTimePasswordForm from '@/brand-components/VetsterOneTimePasswordForm';
import VetsterPasswordLogin from '@/brand-components/VetsterPasswordLogin';
import VetsterResetPassword from '@/brand-components/VetsterResetPassword';
import useBrowserApiHelpers from '@/composables/use-browser-api-helpers';
import AuthFormType from '@/enums/AuthFormType';
import Channel from '@/enums/ChannelType';
import Platform from '@/enums/Platform';
import SubscriptionPlanType from '@/enums/SubscriptionPlanType';
import { isSPA } from '@/helpers/url';
import { i18n } from '@/i18n';
import HasAuthForm from '@/mixins/HasAuthForm';
import ManagesLocaleCookies from '@/mixins/ManagesLocaleCookies';
import ParseErrors from '@/mixins/ParseErrors';
import SocialiteActions from '@/mixins/SocialiteActions';

export default {
  name: 'VetsterAuthLogin',

  components: {
    VetsterAuthSocialiteButtons,
    VetsterNotice,
    VetsterOneTimePasswordForm,
    VetsterPasswordLogin,
    VetsterResetPassword,
  },

  mixins: [
    HasAuthForm,
    ManagesLocaleCookies,
    ParseErrors,
    SocialiteActions,
  ],

  props: {
    showSocial: {
      default: true,
      type: Boolean,
    },
  },

  setup() {
    const { localStorageAvailable } = useBrowserApiHelpers();

    return {
      localStorageAvailable,
      v$: useVuelidate(),
    };
  },

  validations: {
    inputs: {
      email: {
        required,
        email: emailRule,
      },
    },
  },

  data() {
    return {
      AuthFormType,
      mode: AuthFormType.PasswordlessLogin,
      loading: false,
      inputs: {
        email: '',
        code: null,
        password: null,
      },
      error: null,
      passwordResetRequired: false,
      is2FA: false,
    };
  },

  computed: {
    ...mapGetters(['getUser']),
    ...mapGetters('authModal', [
      'authModalMode',
      'isRegistrationHidden',
    ]),
    ...mapGetters('passwordlessLogin', ['isLoading']),
    ...mapGetters('emailVerification', [
      'getChannel',
      'getEmail',
      'getPhoneNumber',
      'getSocialite',
      'showEmailVerification',
    ]),
    getAuthPayload() {
      return omitBy({
        code: this.inputs.code,
        password: this.inputs.password,
        email: this.inputs.email.toLowerCase(),
        platform: Platform.Web,
      }, isNull);
    },
    getConfirmPayload() {
      return {
        code: this.inputs.code,
        provider_type: this.getSocialite?.provider_type,
        access_token: this.getSocialite?.access_token,
      };
    },
    getLocale() {
      return i18n.locale;
    },
    isContinueLoading() {
      return this.loading || this.isLoading;
    },
    isPasswordless() {
      return this.inputs.code && !this.is2FA;
    },
    isPetMeds() {
      return this.getUser?.organization?.name === 'PetMeds';
    },
    isVFB() {
      return [SubscriptionPlanType.Vfw, SubscriptionPlanType.Vfg].includes(this.getUser?.organization?.type);
    },
  },

  watch: {
    showEmailVerification(value) {
      if (value) {
        this.switchMode(AuthFormType.OTP);
      }
    },
    getChannel(channel) {
      switch (channel) {
        case Channel.Sms:
        case Channel.Email:
          this.switchMode(AuthFormType.OTP);
          break;
        case Channel.Password:
          this.switchMode(AuthFormType.PasswordLogin);
          break;
        default:
          break;
      }
    },
    authModalMode(mode) {
      if (mode !== this.mode) {
        this.switchMode(mode);
      }
    },
  },

  created() {
    this.clearErrors();
    this.clearError();
    this.$analytics.trackGA({ eventName: 'gx:identity:login:load' });

    try {
      if (this.localStorageAvailable()) {
        this.inputs.email = window.localStorage.temporaryEmail ?? '';
      }
    } catch {
      //
    }
  },

  mounted() {
    if (this.mode === AuthFormType.PasswordlessLogin && this.$screen('desktop')) {
      this.$refs.emailInput.focus();
    }

    this.$bus.$on('trigger-passwordless-login', this.attemptPasswordlessLogin);
  },

  beforeDestroy() {
    this.$bus.$off('trigger-passwordless-login', this.attemptPasswordlessLogin);
  },

  methods: {
    ...mapActions([
      'checkAuthAndUser',
    ]),
    ...mapActions('authModal', [
      'setAuthModalMode',
      'closeAuthModal',
    ]),
    ...mapActions('passwordlessLogin', [
      'verifyPasswordlessLoginEmail',
    ]),
    passThroughEvent(event) {
      this.$emit('action-completed', event);
    },
    async attemptPasswordlessLogin(email) {
      if (typeof email === 'string') {
        this.inputs.email = email;
      }

      this.v$.inputs.email.$reset();
      this.v$.inputs.email.$touch();

      if (this.v$.inputs.email.$invalid) {
        this.shakeErrors = true;
        this.removeShake();

        return;
      }

      this.passwordResetRequired = false;

      try {
        await this.verifyPasswordlessLoginEmail({
          channel: Channel.Sms,
          email: this.inputs.email,
        });
      } catch (error) {
        const data = error?.response?.data?.data;

        if (data?.password_reset_required) {
          this.passwordResetRequired = true;

          return;
        }

        if (error?.response?.status === 429) {
          this.$toast(this.$t('errors.too-many-attempts'), {
            status: 'error',
            timeout: 20000,
          });

          return;
        }

        if (this.isRegistrationHidden) {
          this.$toast(this.$t('login.could-not-verify-your-details'), {
            status: 'error',
            timeout: 5000,
          });
        } else {
          this.$emit('register', this.inputs.email);
          this.switchMode(AuthFormType.Register);
        }
      }
    },
    async authenticateWithCode(code) {
      this.inputs.code = code;
      if (this.showEmailVerification) {
        await this.confirmEmailVerificationCode();

        return;
      }

      await this.authenticate();
    },
    async authenticateWithPassword(payload) {
      this.inputs.email = payload.email;
      this.inputs.password = payload.password;
      await this.authenticate();
    },
    authenticate() {
      this.passwordResetRequired = false;
      this.clearErrors();
      this.clearError();
      this.v$.inputs.$reset();
      this.v$.inputs.$touch();
      this.$analytics.trackGA({ eventName: 'gx:identity:login:submit-attempt' });
      if (this.v$.inputs.$invalid) {
        this.shakeErrors = true;
        this.removeShake();
        this.$analytics.trackGA({ eventName: 'gx:identity:login:submit-error' });

        return;
      }
      this.loading = true;
      const type = this.isPasswordless
        ? 'email_passwordless'
        : 'email_password';

      this.login().then(
        async () => {
          if (await this.$auth.authenticate()) {
            await this.onAuthenticated(type);
          }
        },
        async (error) => {
          const data = error?.response?.data?.data;

          if (data?.password_reset_required) {
            this.passwordResetRequired = true;

            return;
          }

          if (data?.requires_verification) {
            this.$store.commit('emailVerification/setVerificationData', data);
            this.is2FA = true;

            return;
          }

          if (this.inputs.code) {
            this.error = data?.code[0];
            if (!this.error && error?.response?.status === 401) {
              this.$toast(this.$t('login.one-time-login-code-has-expired'), {
                status: 'error',
                timeout: 20000,
              });
              this.switchMode('login');
            }
          } else {
            this.error = error;
          }

          this.$analytics.trackSignInFailure(type);
        },
      ).finally(() => {
        this.loading = false;
      });
    },
    async confirmEmailVerificationCode() {
      this.loading = true;

      this.$auth.verifyEmailVerificationCode(this.getConfirmPayload)
        .then(async () => {
          this.$toast(this.$t('login.success'));

          await this.$store.dispatch('socialite/authenticate', {
            provider_type: this.getSocialite?.provider_type,
            access_token: this.getSocialite?.access_token,
            platform: Platform.Web,
          })
            .then(async () => {
              if (await this.$auth.authenticate()) {
                const type = this.getSocialite?.provider_type;
                this.$store.commit('emailVerification/setSocialite', null);
                await this.onAuthenticated(type);
              }
            })
            .catch(async (error) => {
              if (error?.response?.data?.data?.requires_verification) {
                this.$store.commit('emailVerification/setSocialite', {
                  provider_type: this.getSocialite?.provider_type,
                  access_token: this.getSocialite?.access_token,
                });
                this.$store.commit('emailVerification/showEmailVerification', true);
              } else {
                this.showError(error, true);

                if (this.getSocialite) {
                  await this.socialiteSignOut({ value: this.getSocialite.provider_type });
                }

                this.$analytics.trackSignInFailure(this.getSocialite?.provider_type);
              }
            });
        })
        .catch((error) => {
          if ([422, 404].includes(error?.response?.status)) {
            this.displayError(`${this.$t('login.invalid-code')}, ${this.$t('login.one-time-use-description')}`);
            this.$store.commit('emailVerification/setSocialite', null);
            this.$store.commit('emailVerification/showEmailVerification', false);
            this.switchMode(AuthFormType.PasswordlessLogin);
          } else {
            this.parseError(error);
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    login() {
      return this.isPasswordless
        ? this.$auth.verifyCodeAndLogin(this.getAuthPayload)
        : this.$auth.loginUser(this.getAuthPayload);
    },
    async onAuthenticated(type) {
      this.$toast(this.$t('login.welcome-back'));
      await this.checkAuthAndUser();

      this.$analytics.trackSignIn(type);

      if (this.redirectToDashboard || this.isVFB || this.isPetMeds) {
        this.$nextTick(() => {
          window.location.href = this.dashboardUrl;
        });
      } else {
        this.setLocaleCookies(this.getUser.locale, !!(this.getUser?.locale_override));

        if (!isSPA(window.location.pathname)) {
          this.$nextTick(() => {
            window.location.reload();
          });
        }
        this.closeAuthModal(true);
      }

      this.$emit('action-completed', type);
    },
    clearError() {
      if (this.error) {
        this.error = null;
      }
    },
    switchMode(mode) {
      this.mode = mode;
      this.setAuthModalMode(mode);
      this.inputs.code = null;

      if (this.mode !== AuthFormType.OTP) {
        this.$store.commit('emailVerification/setVerificationData', null);
      }
    },
    forgotPassword() {
      setTimeout(() => {
        this.switchMode(AuthFormType.PasswordReset);
      }, 100);
    },
  },
};
</script>
